<template>
  <div
    class="PassengerChip"
    :class="[chipStateClass, size && `PassengerChip--${size}`]"
  >
    <div class="PassengerChip-content">
      <PersonIdentity
        :name="passengerName"
        :description="passengerType"
        :size
        :active="selected"
        :disabled
      />

      <SeatCell
        v-if="showCell"
        :label="seatCellText"
        :size
        :state="seatCellState"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { getInitials } from '@cvo/domain';
import SeatCell from '../../atoms/SeatCell/SeatCell.vue';
import PersonIdentity from '../PersonIdentity/PersonIdentity.vue';
import type { SeatCellState } from '../../atoms/SeatCell/SeatCell.model';
import type { PassengerChipProps } from './PassengerChip.model';

const props = withDefaults(defineProps<PassengerChipProps>(), {
  size: 'md',
  seatLabel: '',
});

const chipStateClass = computed(() => {
  if (props.disabled) return 'PassengerChip--disabled';
  if (props.selected) return 'PassengerChip--active';
  return undefined;
});

const seatCellText = computed(() => {
  if (props.selected) return props.seatLabel || getInitials(props.passengerName);
  return '--';
});

const seatCellState = computed<SeatCellState>(() => {
  if (props.disabled) return 'disabled';
  if (props.selected) return 'selected';
  return 'available';
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .PassengerChip {
    display: inline-flex;
    border-width: vars.$molecules-passengerchip-border-stroke;
    border-style: solid;
    border-radius: vars.$molecules-passengerchip-border-radius;
    overflow: hidden;
    min-width: 208px;
    cursor: pointer;
    background-color: vars.$molecules-passengerchip-default-bg-color;
    border-color: vars.$molecules-passengerchip-default-border-color;

    .PersonIdentity-name {
      color: vars.$molecules-passengerchip-default-text-color;
    }

    .PersonIdentity-description {
      color: vars.$molecules-passengerchip-default-secondary-text-color;
    }

    &-content {
      display: flex;
      flex: 1;
      gap: vars.$dimensions-d-2;
      align-items: center;
      min-width: 0;

      .PersonIdentity-info {
        line-height: normal;
      }
    }

    .PersonIdentity-name,
    .PersonIdentity-description {
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex-shrink: 0;
    }

    @include responsive.mobile {
      width: 100%;
    }

    &--md {
      padding: vars.$dimensions-d-2;

      .PersonIdentity-name {
        @include text.text('sm', vars.$typography-weight-strong);
      }

      .PersonIdentity-description {
        @include text.text('xs');
      }
    }

    &--sm {
      padding: vars.$dimensions-d-1 vars.$dimensions-d-2;
      min-width: 160px;

      .PersonIdentity-name {
        @include text.text('xs', vars.$typography-weight-strong);
      }

      .PersonIdentity-description {
        @include text.text('xs');
      }

      .PersonIdentity-info {
        gap: vars.$dimensions-d-0-5;
      }
    }

    &--active {
      background-color: vars.$molecules-passengerchip-active-bg-color;
      border-color: vars.$molecules-passengerchip-active-border-color;

      .PersonIdentity-name {
        color: vars.$molecules-passengerchip-active-text-color;
      }

      .PersonIdentity-description {
        color: vars.$molecules-passengerchip-active-secondary-text-color;
      }
    }

    &--disabled {
      background-color: vars.$molecules-passengerchip-disabled-bg-color;
      border-color: vars.$molecules-passengerchip-disabled-border-color;
      cursor: not-allowed;

      .PersonIdentity-name {
        color: vars.$molecules-passengerchip-disabled-text-color;
      }

      .PersonIdentity-description {
        color: vars.$molecules-passengerchip-disabled-secondary-text-color;
      }
    }
  }
}
</style>
